<script>
import { mapState } from 'vuex'

export default {
  data() {
    return {
      originalTheme: null
    }
  },
  computed: {
    ...mapState({
      lightTheme: state => state?.theme?.lightTheme
    }),
    hasChanges() {
      return (
        JSON.stringify(this.lightTheme) !== JSON.stringify(this.originalTheme)
      )
    }
  },
  created() {
    this.$store.dispatch('getThemeColors').then(() => {
      this.originalTheme = JSON.parse(JSON.stringify(this.lightTheme))
    })
  },
  methods: {
    updateColor(key, color) {
      if (color && color.hex) {
        this.$store.commit('SET_LIGHT_THEME', { key, color })
      }
    },
    save() {
      this.$store.dispatch('saveLightTheme').then(() => {
        this.originalTheme = JSON.parse(JSON.stringify(this.lightTheme))
      })
    },
    resetTheme() {
      this.$store.commit('RESET_THEME')
      this.originalTheme = JSON.parse(JSON.stringify(this.lightTheme))
    }
  }
}
</script>

<template>
  <v-container fluid class="pt-0">
    <!-- MAIN COLORS -->
    <v-row class="pt-0">
      <v-col class="pt-0">
        <h3 class="float-left">
          {{ $t('component.whitelabel.color.palette.main.colors') }}
        </h3>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn
              id="reset-default-theme-button"
              class="float-right"
              icon
              color="primary"
              v-on="on"
              @click="resetTheme">
              <v-icon>mdi-autorenew</v-icon>
            </v-btn>
          </template>
          <span>{{
            $t('component.whitelabel.color.palette.reset.button.tooltip')
          }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-divider></v-divider>

    <!-- PRIMARY, SECONDARY, TERTIARY -->
    <v-row id="main-colors-container">
      <v-col
        v-for="(field, i) in ['primary', 'secondary', 'tertiary']"
        :key="i"
        md="6"
        lg="4"
        sm="12">
        <div
          class="color-section d-flex flex-column justify-space-between fill-height">
          <div class="d-flex justify-space-between align-start">
            <h4 class="mb-1">
              {{ $t(`component.whitelabel.color.palette.${field}.color`) }}
            </h4>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{
                $t(`component.whitelabel.color.palette.${field}.description`)
              }}</span>
            </v-tooltip>
          </div>
          <v-color-picker
            :id="`main-color-picker-${field}`"
            v-model="lightTheme[field]"
            hide-mode-switch
            mode="hexa"
            @update:color="color => updateColor(field, color)"
          />
        </div>
      </v-col>
    </v-row>

    <!-- SIDEBAR SECTION -->
    <v-row id="sidebar-colors-container" class="pt-0">
      <v-col class="pt-0">
        <h3>{{ $t('component.whitelabel.color.palette.sidebar.colors') }}</h3>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row>
      <v-col
        v-for="(field, i) in [
          'sidebarBackground',
          'sidebarLink',
          'sidebarChildLink'
        ]"
        :key="i"
        md="6"
        lg="4"
        sm="12">
        <div
          class="color-section d-flex flex-column justify-space-between fill-height">
          <div class="d-flex justify-space-between align-start">
            <h4 class="mb-1">
              {{ $t(`component.whitelabel.color.palette.${field}.color`) }}
            </h4>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">
                  mdi-information-outline</v-icon>
              </template>
              <span>{{
                $t(`component.whitelabel.color.palette.${field}.description`)
              }}</span>
            </v-tooltip>
          </div>
          <v-color-picker
            :id="`sidebar-color-picker-${field}`"
            v-model="lightTheme[field]"
            hide-mode-switch
            mode="hexa"
            @update:color="color => updateColor(field, color)"
          />
        </div>
      </v-col>
    </v-row>

    <!-- NAVBAR SECTION -->
    <v-row id="navbar-colors-container" class="pt-0">
      <v-col class="pt-0">
        <h3>{{ $t('component.whitelabel.color.palette.navbar.colors') }}</h3>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row>
      <v-col
        v-for="(field, i) in ['navbarBackground', 'navbarLink']"
        :key="i"
        md="6"
        lg="6"
        sm="12">
        <div
          class="color-section d-flex flex-column justify-space-between fill-height">
          <div class="d-flex justify-space-between align-start">
            <h4 class="mb-1">
              {{ $t(`component.whitelabel.color.palette.${field}.color`) }}
            </h4>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">
                  mdi-information-outline</v-icon>
              </template>
              <span>{{
                $t(`component.whitelabel.color.palette.${field}.description`)
              }}</span>
            </v-tooltip>
          </div>
          <v-color-picker
            :id="`navbar-color-picker-${field}`"
            v-model="lightTheme[field]"
            hide-mode-switch
            mode="hexa"
            @update:color="color => updateColor(field, color)"
          />
        </div>
      </v-col>
    </v-row>

    <!-- TEXTUAL SECTION -->
    <v-row id="text-colors-container" class="pt-0">
      <v-col class="pt-0">
        <h3>{{ $t('component.whitelabel.color.palette.textual.colors') }}</h3>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row>
      <v-col
        v-for="(field, i) in [
          'headingLight',
          'headingDark',
          'subheadingLight',
          'subheadingDark'
        ]"
        :key="i"
        lg="6"
        md="6"
        sm="12">
        <div
          class="color-section d-flex flex-column justify-space-between fill-height">
          <div class="d-flex justify-space-between align-start">
            <h4 class="mb-1">
              {{ $t(`component.whitelabel.color.palette.${field}.color`) }}
            </h4>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">
                  mdi-information-outline</v-icon>
              </template>
              <span>{{
                $t(`component.whitelabel.color.palette.${field}.description`)
              }}</span>
            </v-tooltip>
          </div>
          <v-color-picker
            :id="`text-color-picker-${field}`"
            v-model="lightTheme[field]"
            hide-mode-switch
            mode="hexa"
            @update:color="color => updateColor(field, color)"
          />
        </div>
      </v-col>
    </v-row>

    <!-- FEEDBACK SECTION -->
    <v-row id="feedback-colors-container" class="pt-0">
      <v-col class="pt-0">
        <h3>{{ $t('component.whitelabel.color.palette.feedback.colors') }}</h3>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-row>
      <v-col
        v-for="(field, i) in ['info', 'success', 'warning', 'error']"
        :key="i"
        lg="6"
        md="6"
        sm="12">
        <div
          class="color-section d-flex flex-column justify-space-between fill-height">
          <div class="d-flex justify-space-between align-start">
            <h4 class="mb-1">
              {{ $t(`component.whitelabel.color.palette.${field}.color`) }}
            </h4>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" small class="ml-2 mt-1" v-on="on">
                  mdi-information-outline</v-icon>
              </template>
              <span>{{
                $t(`component.whitelabel.color.palette.${field}.description`)
              }}</span>
            </v-tooltip>
          </div>
          <v-color-picker
            :id="`text-color-picker-${field}`"
            v-model="lightTheme[field]"
            hide-mode-switch
            mode="hexa"
            @update:color="color => updateColor(field, color)"
          />
        </div>
      </v-col>
    </v-row>

    <!-- SAVE BUTTON -->
    <v-row>
      <v-col>
        <v-btn v-show="hasChanges" id="save-theme-colors-button" color="primary" block @click="save">
          <v-icon class="pe-3">mdi-check</v-icon>
          {{ $t('button.save') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>
.text-description {
  font-size: 16px;
  color: grey;
}
.color-section {
  min-height: 200px;
}
.v-icon {
  cursor: pointer;
}
</style>
